import Button from '@mui/material/Button';
import Modal from '@mui/material/Modal';
import TextField from '@mui/material/TextField';
import { styled } from '@mui/system';
import { FC, useEffect, useState } from 'react';
import { FcFolder, FcOpenedFolder } from 'react-icons/fc';
import { MdArrowBack } from 'react-icons/md';
import { connectField } from 'uniforms';
import { useIcon } from '../Hooks/useIcon';
import AllIcons, {
  AllRanksIcons,
  FolderNames,
  GeneralIcons,
  Icons,
  JobSafetyIcons,
  OfficeIcons,
  ShipIcons,
} from './constants';

// Styled components using MUI's styled API
const RootDiv = styled('div')({
  marginTop: 10,
  display: 'flex',
  alignItems: 'center',

  '& svg': {
    cursor: 'pointer',
  },
});

const IconSelectIconsDiv = styled('div')({
  display: 'flex',
  flexWrap: 'wrap',
  textAlign: 'center',
  border: '1px solid #ccc',
  borderRadius: 8,
  padding: 4,
  height: 400,
  overflowY: 'auto',
  alignContent: 'flex-start',
});

const PaperDiv = styled('div')(({ theme }: any) => ({
  position: 'absolute',
  backgroundColor: theme.palette.background.paper,
  boxShadow: theme.shadows[5],
  padding: '5px',
  borderRadius: '4px',
  outline: 'none',
  width: 600,
}));

const IconElementDiv = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  margin: '10px 5px',
  cursor: 'pointer',
  width: '100px',
  padding: '5px',
  borderRadius: 4,
});

const IconFieldIconDiv = styled('div')({
  fontSize: 42,
  borderRadius: '4px',
  border: '1px solid #fff',
});

const IconFolderIconDiv = styled('div')({
  fontSize: 42,
  padding: '5px',
  borderRadius: '4px',
  border: '1px solid #fff',
});

const IconFieldNameDiv = styled('div')({
  fontSize: '.7rem',
  marginTop: '-.5rem',
  overflow: 'hidden',
  wordBreak: 'break-all',
});

const IconFolderNameDiv = styled('div')({
  fontSize: '.7rem',
  marginTop: '-1rem',
  overflow: 'hidden',
  wordBreak: 'break-all',
});

const IconContainerDiv = styled('div')({
  height: 100,
});

const IconSelectedDiv = styled('div')({
  fontSize: '20px',
  padding: '3px 10px',
  border: '1px solid #ccc',
  marginLeft: '5px',
  paddingTop: '10px',
  borderRadius: '4px',
});

// Define the props type for IconField component
interface IconFieldProps {
  value: string;
  onChange: (value: string) => void;
  label: string;
}

export const IconField = connectField(({ value, onChange, label }: any) => {
  const [IsFolderSelected, setIsFolderSelected] = useState(false);
  const [FolderSelected, setFolderSelected] = useState('');
  const [open, setOpen] = useState(false);
  const [RenderedIcons, setRenderedIcons] = useState<string[]>([]);
  const [FocusedIcon, setFocusedIcon] = useState('');
  const [IsHovered, setIsHovered] = useState('');
  const [Search, setSearch] = useState('');
  const [SelectedIcon, setSelectedIcon] = useState('');

  useEffect(() => {
    if (Search) {
      const filteredIcons = AllIcons.filter((i) =>
        i.toLowerCase().includes(Search.toLowerCase())
      );
      setRenderedIcons(filteredIcons);
      setIsFolderSelected(true);
      setFolderSelected('All Icons');
    } else {
      setRenderedIcons(Icons);
    }
  }, [Search]);

  const handleOpen = () => {
    setOpen(true);
  };
  const handleClose = () => {
    setOpen(false);
  };

  const getRenderedIconsByFolder = (folderSelected: string) => {
    switch (folderSelected) {
      case 'All Ranks':
        setRenderedIcons(AllRanksIcons);
        break;
      case 'General':
        setRenderedIcons(GeneralIcons);
        break;
      case 'Job Safety':
        setRenderedIcons(JobSafetyIcons);
        break;
      case 'Office':
        setRenderedIcons(OfficeIcons);
        break;
      case 'Ship':
        setRenderedIcons(ShipIcons);
        break;
      case 'Misc':
        setRenderedIcons(Icons);
        break;
      case 'All Icons':
        setRenderedIcons(AllIcons);
        break;

      default:
        setRenderedIcons(Icons);
        break;
    }
  };

  return (
    <RootDiv>
      <label htmlFor="textAlignSelect">Icon:</label>

      <div className="icon-selected">
        <div onClick={handleOpen}>
          <Button className="ml-1" variant={'contained'}>
            Select Icon
          </Button>
        </div>
      </div>

      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="simple-modal-title"
        aria-describedby="simple-modal-description"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <PaperDiv>
          <div className="icon-select-header flex jcsb">
            {IsFolderSelected ? (
              <div className="flex ai-center">
                <MdArrowBack
                  style={{ fontSize: 32 }}
                  onClick={() => {
                    setIsFolderSelected(false);
                    setFocusedIcon('');
                    setIsHovered('');
                  }}
                />
                <h2 style={{ marginLeft: 5 }}>{FolderSelected}</h2>
              </div>
            ) : (
              <h2 style={{ marginLeft: 5 }}>Select Folder</h2>
            )}
            <div className="flex-center-align">
              <label>Search icons</label>
              <TextField
                id={'iconName'}
                name={'iconName'}
                type={'text'}
                className={'ml-1'}
                inputProps={{ maxLength: 30 }}
                onChange={(e) => setSearch(e.target.value)}
                value={Search}
                variant="outlined"
              />
            </div>
          </div>
          <IconSelectIconsDiv id="selectIcons">
            {!IsFolderSelected
              ? FolderNames.map((i) => (
                  <div
                    key={i}
                    style={{ height: 120 }}
                    onMouseEnter={() => setIsHovered(i)}
                    onClick={() => {
                      setIsFolderSelected(true);
                      setFolderSelected(i);
                      getRenderedIconsByFolder(i);
                    }}
                  >
                    <FolderElement iconName={i} isSelected={IsHovered === i} />
                  </div>
                ))
              : RenderedIcons.map((i) => (
                  <IconContainerDiv key={i} onClick={() => setFocusedIcon(i)}>
                    <IconElement iconName={i} isSelected={FocusedIcon === i} />
                  </IconContainerDiv>
                ))}
          </IconSelectIconsDiv>
          <div className="flex icon-field-actions">
            <Button variant="contained" onClick={() => handleClose()}>
              Close
            </Button>

            <Button
              id="formSubmitBtn"
              type="submit"
              variant="contained"
              color="primary"
              onClick={() => {
                setSelectedIcon(FocusedIcon);
                onChange(FocusedIcon);
                handleClose();
              }}
              disabled={!FocusedIcon}
            >
              Apply
            </Button>
          </div>
        </PaperDiv>
      </Modal>
    </RootDiv>
  );
});

interface FolderElementProps {
  iconName: string;
  isSelected: boolean;
}

const FolderElement: FC<FolderElementProps> = ({ iconName, isSelected }) => (
  <IconElementDiv>
    <IconFolderIconDiv>
      {isSelected ? <FcOpenedFolder /> : <FcFolder />}
    </IconFolderIconDiv>
    <IconFolderNameDiv style={{ fontWeight: isSelected ? '700' : 'initial' }}>
      {iconName}
    </IconFolderNameDiv>
  </IconElementDiv>
);

interface IconElementProps {
  iconName: string;
  isSelected: boolean;
}

export const IconElement: FC<IconElementProps> = ({ iconName, isSelected }) => {
  const Icon = useIcon({ iconName });
  return (
    <IconElementDiv
      style={{ border: isSelected ? '1px solid #e91e63' : 'none', gap: '10px' }}
    >
      <IconFieldIconDiv>{Icon}</IconFieldIconDiv>
      <IconFieldNameDiv>{iconName}</IconFieldNameDiv>
    </IconElementDiv>
  );
};

interface IconDisplayProps {
  iconName: string;
}

export const IconDisplay: FC<IconDisplayProps> = ({ iconName }) => {
  const Icon = useIcon({ iconName });
  return <>{Icon}</>;
};
